<script>
  import { createEventDispatcher } from 'svelte';
  import Button from '@components/Button.svelte';
  import CircleCropper from '@utils/circle-cropper';
  import ImageUpload from '@utils/image-upload';
  import UserAjax from '@ajax/user';
  import queryString from 'query-string';
  import Track from '@utils/track';

  const dispatch = createEventDispatcher();
  import { getCurrentUserStore } from '@stores/current_user';

  let user = getCurrentUserStore('default');

  export let mobile = false;
  export let createdUser;
  export let prefilledProfileImageUrl;
  export let inviteCode;

  let tiktokAdCodes = ['gf11f', 'qymg3', 'gf11g', 'h6fg1', '3srhv', '236c3', 'y5g4q'];
  let sf = typeof window !== 'undefined' && queryString.parse(window.location.search).sf;

  let uploadedSrc = prefilledProfileImageUrl;
  let fileInput;
  let showContinueOnAppScreen = false;

  let attachImageUpload = function (node) {
    ImageUpload.attach(
      node,
      function (data) {
        if (data && data.length > 0 && data[0] && data[0].uploadURL) {
          uploadedSrc = data[0].uploadURL;
          UserAjax.update(createdUser, { avatar_url: uploadedSrc });
          user.set({ ...createdUser, avatar_url: uploadedSrc });
        }
      },
      () => {},
      {
        maxNumberOfFiles: 1,
        cropper: CircleCropper(),
      }
    );
  };

  let uploadFile = function () {
    Track.addProfilePhotoClick();
    fileInput.click();
  };

  const downloadApp = () => {
    let storeUrl = `https://benable.onelink.me/4GPu/invite`;

    if (sf && sf.length > 0) {
      storeUrl += `?sf=${sf}`;
    }

    window.location.href = storeUrl;
  };

  let shouldContinueOnApp = function () {
    if (isTikTokBrowser()) {
      return false;
    }

    if (getDeviceType() === null) {
      return false;
    }

    if (createdUser && createdUser.id % 2 === 0) {
      let code = inviteCode && inviteCode.toLowerCase();
      return code && tiktokAdCodes.includes(code);
    } else {
      return false;
    }
  };

  let continueOnApp = function () {
    document.getElementsByClassName('progress-bar')[0].style.display = 'none';
    UserAjax.markAsNeedsOnboardingMobileApp();
    showContinueOnAppScreen = true;
    user.set({ ...createdUser, needs_onboarding_mobile_app: true });
  };

  let handleFinished = function () {
    if (shouldContinueOnApp()) {
      continueOnApp();
    } else {
      Track.profilePhotoNextClick();
      dispatch('finished');
    }
  };

  let handleSkip = function () {
    if (shouldContinueOnApp()) {
      continueOnApp();
    } else {
      Track.profilePhotoSkipClick();
      dispatch('finished');
    }
  };

  const isTikTokBrowser = () => {
    try {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return userAgent.includes('musical_ly') || userAgent.includes('BytedanceWebview');
    } catch {
      return false;
    }
  };

  let getDeviceType = function () {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroid = /android/i.test(userAgent);

    if (isIOS) {
      return 'ios';
    } else if (isAndroid) {
      return 'android';
    } else {
      return null;
    }
  };
</script>

<style style="text/scss" lang="scss">
  .photo-upload {
    min-height: calc(100dvh - 114px);
    display: flex;
    padding: 20px;

    .navigation-buttons {
      position: absolute;
      top: 46px;
      left: 0;
      width: 100%;
      display: flex;
      justify-content: flex-end;
      padding: 0 20px;
    }

    .back-btn,
    .skip-btn {
      font-weight: 500;
      font-size: 14px;
      color: #1a1a1a;
      cursor: pointer;
      border: none;
      padding: 0 !important;
      background: none;

      &:hover {
        color: #3d3d3d;
      }
    }

    .content-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      max-width: 440px;
      margin: 0 auto;
      gap: 32px;
      flex: 1;
      padding-bottom: 44px;
    }

    .ui-title {
      font-size: 42px;
      line-height: 38px;
      text-align: center;
      font-weight: 800;
      text-transform: uppercase;
      margin-top: 64px;
      color: #717171;
    }

    .ui-text {
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #7f7f7f;
    }

    .ui-text-black {
      font-weight: 500;
      color: #3d3d3d;
    }

    .profile-info {
      display: flex;
      flex-direction: column;
      padding: 0;
      gap: 32px;
    }

    .photo-circle {
      height: 192px;
      width: 192px;
      border-radius: 9999px;
      border: 2px dashed #a893f9;
      background: #f5f3fc;
      margin-left: auto;
      margin-right: auto;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      &:hover {
        border: 2px dashed #896bd9;

        svg {
          path {
            fill: #896bd9;
          }
        }
      }

      svg {
        margin-top: -6px;
        width: 89px;
        height: 72px;
      }
    }

    .uploaded-image-container {
      margin-left: auto;
      margin-right: auto;
      height: 192px;
      width: 192px;
      cursor: pointer;

      .uploaded-image {
        height: 100%;
        width: 100%;
        width: 100%;
        border-radius: 9999px;
        object-fit: contain;
      }
    }

    .tap-to-change {
      font-size: 13px;
      line-height: 20px;
      color: #7f7f7f;
      margin-top: 20px;
    }

    .button-container {
      width: 100%;
    }
  }

  .continue-on-app-screen {
    padding-left: 20px;
    padding-right: 20px;

    .ui-title {
      margin-top: 30px;
      padding-top: 40px;
      font-size: 24px;
      font-weight: 700;
    }

    .ui-subtitle {
      margin-top: 20px;
      color: #555555;
    }

    .device-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-top: 60px;

      .store-buttons {
        display: flex;
        justify-content: center;
        margin-bottom: 40px;

        @media (max-width: 600px) {
          flex-direction: column;
          align-items: center;
        }
      }

      .store-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
        transition: transform 0.2s ease;
        margin-bottom: 40px;

        &:hover {
          transform: translateY(-3px);
        }

        img {
          height: 40px;
          margin-right: 20px;
        }

        span {
          font-weight: bold;
        }
      }

      .ios-button {
        background: #000;
        color: white;
      }

      .android-button {
        background: #689f38;
        color: white;
      }
    }
  }

  .mobile {
    &.photo-upload {
      min-height: calc(100dvh - 60px);
    }

    .ui-title {
      margin-top: 32px;
    }

    &.photo-upload .navigation-buttons {
      top: 18px;
    }

    .photo-circle {
      width: 154px;
      height: 154px;
    }

    .uploaded-image-container {
      width: 154px;
      height: 154px;
    }
  }
</style>

<div bind:this={fileInput} style="display: none" use:attachImageUpload />

{#if createdUser}
  {#if showContinueOnAppScreen}
    {@const deviceType = getDeviceType()}

    <div class="continue-on-app-screen">
      <div class="ui-title">Continue on the app!</div>
      <div class="ui-subtitle">Download the app to start sharing your recommendations.</div>
      <div class="device-button">
        {#if deviceType === 'ios'}
          <div class="store-button ios-button" on:click={downloadApp}>
            <img src="/images/app-icons/apple.png" alt="Download on the App Store" />
            <span>Download on the<br />App Store</span>
          </div>
        {:else if deviceType === 'android'}
          <div class="store-button android-button" on:click={downloadApp}>
            <img src="/images/app-icons/google.png" alt="Get it on Google Play" />
            <span>Get it on<br />Google Play</span>
          </div>
        {:else}
          <div class="store-button ios-button" on:click={downloadApp}>
            <img src="/images/app-icons/apple.png" alt="Download on the App Store" />
            <span>Download on the<br />App Store</span>
          </div>
          <div class="store-button android-button" on:click={downloadApp}>
            <img src="/images/app-icons/google.png" alt="Get it on Google Play" />
            <span>Get it on<br />Google Play</span>
          </div>
        {/if}
      </div>
    </div>
  {:else}
    <div class="photo-upload" class:mobile={mobile}>
      <div class="navigation-buttons">
        {#if !uploadedSrc}
          <button class="skip-btn test-photo-skip-btn created-account-tiktok-event" on:click={handleSkip}>Skip</button>
        {/if}
      </div>
      <div class="content-container">
        <div class="profile-info">
          <div class="ui-title national-2-narrow">Add profile photo</div>
          {#if uploadedSrc}
            <div>
              <div class="uploaded-image-container" on:click={uploadFile}>
                <img alt="uploaded-image" class="uploaded-image" src={uploadedSrc} />
              </div>
              <div class="tap-to-change">Tap photo to change</div>
            </div>
          {:else}
            <div class="photo-circle" on:click={uploadFile}>
              <svg fill="none" height="88" viewBox="0 0 89 88" width="89" xmlns="http://www.w3.org/2000/svg"
                ><g fill="#a893f9"
                  ><path
                    d="m1.23716 73.004h1.75v5.824c0 .336.014.6767.042 1.022.03733.3453.13066.658.28.938.15866.2707.392.4947.7.672.31733.1773.76066.266 1.33.266.56933 0 1.008-.0887 1.316-.266.31733-.1773.55066-.4013.7-.672.15866-.28.252-.5927.28-.938.03733-.3453.056-.686.056-1.022v-5.824h1.75v6.398c0 .644-.098 1.204-.294 1.68s-.476.8773-.84 1.204c-.35467.3173-.784.5553-1.288.714s-1.064.238-1.68.238-1.176-.0793-1.68-.238-.938-.3967-1.302-.714c-.35467-.3267-.63-.728-.826-1.204s-.294-1.036-.294-1.68zm10.03354 2.758h1.512v.98h.028c.224-.42.5366-.7187.938-.896.4013-.1867.8353-.28 1.302-.28.5693 0 1.064.1027 1.484.308.4293.196.784.4713 1.064.826.28.3453.49.7513.63 1.218s.21.966.21 1.498c0 .4853-.0654.9567-.196 1.414-.1214.4573-.3127.8633-.574 1.218-.252.3453-.574.6253-.966.84-.392.2053-.854.308-1.386.308-.2334 0-.4667-.0233-.7-.07-.2334-.0373-.4574-.1027-.672-.196-.2147-.0933-.4154-.21-.602-.35-.1774-.1493-.3267-.322-.448-.518h-.028v3.612h-1.596zm5.572 3.626c0-.3267-.042-.644-.126-.952s-.21-.5787-.378-.812c-.168-.2427-.378-.434-.63-.574-.252-.1493-.5414-.224-.868-.224-.672 0-1.1807.2333-1.526.7-.336.4667-.504 1.0873-.504 1.862 0 .364.042.7047.126 1.022.0933.308.2286.574.406.798.1773.224.3873.4013.63.532.252.1307.5413.196.868.196.364 0 .672-.0747.924-.224s.4573-.3407.616-.574c.168-.2427.2846-.5133.35-.812.0746-.308.112-.6207.112-.938zm2.9866-6.384h1.596v9.996h-1.596zm6.6529 10.192c-.5786 0-1.0966-.0933-1.554-.28-.448-.196-.8306-.462-1.148-.798-.308-.336-.546-.7373-.714-1.204-.1586-.4667-.238-.98-.238-1.54 0-.5507.0794-1.0593.238-1.526.168-.4667.406-.868.714-1.204.3174-.336.7-.5973 1.148-.784.4574-.196.9754-.294 1.554-.294.5787 0 1.092.098 1.54.294.4574.1867.84.448 1.148.784.3174.336.5554.7373.714 1.204.168.4667.252.9753.252 1.526 0 .56-.084 1.0733-.252 1.54-.1586.4667-.3966.868-.714 1.204-.308.336-.6906.602-1.148.798-.448.1867-.9613.28-1.54.28zm0-1.26c.3547 0 .6627-.0747.924-.224.2614-.1493.476-.3453.644-.588s.2894-.5133.364-.812c.084-.308.126-.6207.126-.938 0-.308-.042-.616-.126-.924-.0746-.308-.196-.5787-.364-.812-.168-.2427-.3826-.4387-.644-.588-.2613-.1493-.5693-.224-.924-.224-.3546 0-.6626.0747-.924.224-.2613.1493-.476.3453-.644.588-.168.2333-.294.504-.378.812-.0746.308-.112.616-.112.924 0 .3173.0374.63.112.938.084.2987.21.5693.378.812s.3827.4387.644.588c.2614.1493.5694.224.924.224zm11.0428-.546c0 .196.0234.336.07.42.056.084.1587.126.308.126h.168c.0654 0 .14-.0093.224-.028v1.106c-.056.0187-.1306.0373-.224.056-.084.028-.1726.0513-.266.07-.0933.0187-.1866.0327-.28.042-.0933.0093-.1726.014-.238.014-.3266 0-.5973-.0653-.812-.196-.2146-.1307-.3546-.3593-.42-.686-.3173.308-.7093.532-1.176.672-.4573.14-.9006.21-1.33.21-.3266 0-.6393-.0467-.938-.14-.2986-.084-.5646-.21-.798-.378-.224-.1773-.406-.3967-.546-.658-.1306-.2707-.196-.5833-.196-.938 0-.448.0794-.812.238-1.092.168-.28.3827-.4993.644-.658.2707-.1587.5694-.2707.896-.336.336-.0747.672-.1307 1.008-.168.2894-.056.5647-.0933.826-.112.2614-.028.49-.07.686-.126.2054-.056.364-.14.476-.252.1214-.1213.182-.2987.182-.532 0-.2053-.0513-.3733-.154-.504-.0933-.1307-.2146-.2287-.364-.294-.14-.0747-.2986-.1213-.476-.14-.1773-.028-.3453-.042-.504-.042-.448 0-.8166.0933-1.106.28-.2893.1867-.4526.476-.49.868h-1.596c.028-.4667.14-.854.336-1.162s.4434-.5553.742-.742c.308-.1867.6534-.3173 1.036-.392.3827-.0747.7747-.112 1.176-.112.3547 0 .7047.0373 1.05.112.3454.0747.6534.196.924.364.28.168.504.3873.672.658.168.2613.252.5833.252.966zm-1.596-2.016c-.2426.1587-.5413.2567-.896.294-.3546.028-.7093.0747-1.064.14-.168.028-.3313.07-.49.126-.1586.0467-.2986.1167-.42.21-.1213.084-.2193.2007-.294.35-.0653.14-.098.3127-.098.518 0 .1773.0514.3267.154.448.1027.1213.224.2193.364.294.1494.0653.308.112.476.14.1774.028.336.042.476.042.1774 0 .3687-.0233.574-.07.2054-.0467.3967-.126.574-.238.1867-.112.3407-.252.462-.42.1214-.1773.182-.392.182-.644zm10.1313 3.626h-1.512v-.98h-.028c-.2146.42-.5273.7233-.938.91-.4106.1773-.8446.266-1.302.266-.5693 0-1.0686-.098-1.498-.294-.42-.2053-.77-.4807-1.05-.826s-.49-.7513-.63-1.218c-.14-.476-.21-.9847-.21-1.526 0-.6533.0887-1.218.266-1.694.1774-.476.4107-.868.7-1.176.2987-.308.6347-.532 1.008-.672.3827-.1493.77-.224 1.162-.224.224 0 .4527.0233.686.07.2334.0373.4574.1027.672.196.2147.0933.4107.2147.588.364.1867.14.3407.308.462.504h.028v-3.696h1.596zm-5.572-3.542c0 .308.0374.6113.112.91.084.2987.2054.5647.364.798.168.2333.378.42.63.56s.5507.21.896.21c.3547 0 .658-.0747.91-.224.2614-.1493.4714-.3453.63-.588.168-.2427.2894-.5133.364-.812.084-.308.126-.6207.126-.938 0-.8027-.182-1.428-.546-1.876-.3546-.448-.84-.672-1.456-.672-.3733 0-.6906.0793-.952.238-.252.1493-.462.35-.63.602-.1586.2427-.2753.5227-.35.84-.0653.308-.098.6253-.098.952zm11.2511-3.696h1.512v.98h.028c.224-.42.5367-.7187.938-.896.4014-.1867.8354-.28 1.302-.28.5694 0 1.064.1027 1.484.308.4294.196.784.4713 1.064.826.28.3453.49.7513.63 1.218s.21.966.21 1.498c0 .4853-.0653.9567-.196 1.414-.1213.4573-.3126.8633-.574 1.218-.252.3453-.574.6253-.966.84-.392.2053-.854.308-1.386.308-.2333 0-.4666-.0233-.7-.07-.2333-.0373-.4573-.1027-.672-.196-.2146-.0933-.4153-.21-.602-.35-.1773-.1493-.3266-.322-.448-.518h-.028v3.612h-1.596zm5.572 3.626c0-.3267-.042-.644-.126-.952s-.21-.5787-.378-.812c-.168-.2427-.378-.434-.63-.574-.252-.1493-.5413-.224-.868-.224-.672 0-1.1806.2333-1.526.7-.336.4667-.504 1.0873-.504 1.862 0 .364.042.7047.126 1.022.0934.308.2287.574.406.798.1774.224.3874.4013.63.532.252.1307.5414.196.868.196.364 0 .672-.0747.924-.224s.4574-.3407.616-.574c.168-.2427.2847-.5133.35-.812.0747-.308.112-.6207.112-.938zm2.9446-6.384h1.596v3.71h.028c.196-.3267.4853-.5973.868-.812.392-.224.826-.336 1.302-.336.7933 0 1.4187.2053 1.876.616s.686 1.0267.686 1.848v4.97h-1.596v-4.55c-.0187-.5693-.14-.98-.364-1.232-.224-.2613-.574-.392-1.05-.392-.2707 0-.5133.0513-.728.154-.2147.0933-.3967.2287-.546.406-.1493.168-.266.3687-.35.602s-.126.4807-.126.742v4.27h-1.596zm11.3571 10.192c-.5787 0-1.0967-.0933-1.554-.28-.448-.196-.8307-.462-1.148-.798-.308-.336-.546-.7373-.714-1.204-.1587-.4667-.238-.98-.238-1.54 0-.5507.0793-1.0593.238-1.526.168-.4667.406-.868.714-1.204.3173-.336.7-.5973 1.148-.784.4573-.196.9753-.294 1.554-.294.5786 0 1.092.098 1.54.294.4573.1867.84.448 1.148.784.3173.336.5553.7373.714 1.204.168.4667.252.9753.252 1.526 0 .56-.084 1.0733-.252 1.54-.1587.4667-.3967.868-.714 1.204-.308.336-.6907.602-1.148.798-.448.1867-.9614.28-1.54.28zm0-1.26c.3546 0 .6626-.0747.924-.224.2613-.1493.476-.3453.644-.588s.2893-.5133.364-.812c.084-.308.126-.6207.126-.938 0-.308-.042-.616-.126-.924-.0747-.308-.196-.5787-.364-.812-.168-.2427-.3827-.4387-.644-.588-.2614-.1493-.5694-.224-.924-.224-.3547 0-.6627.0747-.924.224-.2614.1493-.476.3453-.644.588-.168.2333-.294.504-.378.812-.0747.308-.112.616-.112.924 0 .3173.0373.63.112.938.084.2987.21.5693.378.812s.3826.4387.644.588c.2613.1493.5693.224.924.224zm4.2528-6.174h1.204v-2.17h1.596v2.17h1.442v1.19h-1.442v3.864c0 .168.0047.3127.014.434.0187.1213.0513.224.098.308.056.084.1353.1493.238.196.1027.0373.2427.056.42.056h.336c.112-.0093.224-.028.336-.056v1.232c-.1773.0187-.35.0373-.518.056s-.3407.028-.518.028c-.42 0-.7607-.0373-1.022-.112-.252-.084-.4527-.2007-.602-.35-.14-.1587-.238-.3547-.294-.588-.0467-.2333-.0747-.4993-.084-.798v-4.27h-1.204zm8.7081 7.434c-.5787 0-1.0967-.0933-1.554-.28-.448-.196-.8307-.462-1.148-.798-.308-.336-.546-.7373-.714-1.204-.1587-.4667-.238-.98-.238-1.54 0-.5507.0793-1.0593.238-1.526.168-.4667.406-.868.714-1.204.3173-.336.7-.5973 1.148-.784.4573-.196.9753-.294 1.554-.294s1.092.098 1.54.294c.4573.1867.84.448 1.148.784.3173.336.5553.7373.714 1.204.168.4667.252.9753.252 1.526 0 .56-.084 1.0733-.252 1.54-.1587.4667-.3967.868-.714 1.204-.308.336-.6907.602-1.148.798-.448.1867-.9613.28-1.54.28zm0-1.26c.3547 0 .6627-.0747.924-.224s.476-.3453.644-.588.2893-.5133.364-.812c.084-.308.126-.6207.126-.938 0-.308-.042-.616-.126-.924-.0747-.308-.196-.5787-.364-.812-.168-.2427-.3827-.4387-.644-.588s-.5693-.224-.924-.224-.6627.0747-.924.224-.476.3453-.644.588c-.168.2333-.294.504-.378.812-.0747.308-.112.616-.112.924 0 .3173.0373.63.112.938.084.2987.21.5693.378.812s.3827.4387.644.588.5693.224.924.224z" /><path
                    d="m43 19.1539c-1.5823 0-3.129.4737-4.4446 1.3612s-2.3409 2.1489-2.9464 3.6248-.764 3.0999-.4553 4.6666c.3087 1.5668 1.0706 3.006 2.1894 4.1355 1.1189 1.1296 2.5443 1.8989 4.0962 2.2105 1.5518.3117 3.1604.1517 4.6222-.4596s2.7112-1.6466 3.5903-2.9748c.879-1.3283 1.3482-2.8898 1.3482-4.4873 0-2.1421-.8429-4.1965-2.3431-5.7113-1.5003-1.5147-3.5352-2.3656-5.6569-2.3656zm17.6-9.69234h-4.8l-2.16-4.37769c-.3126-.62855-.7927-1.15658-1.3862-1.52472-.5936-.36813-1.2772-.56177-1.9738-.55912h-14.56c-.6966-.00265-1.3802.19099-1.9738.55912-.5935.36814-1.0736.89617-1.3862 1.52472l-2.16 4.37769h-4.8c-1.6974 0-3.3253.68074-4.5255 1.89254s-1.8745 2.8553-1.8745 4.569v22.6154c0 1.7137.6743 3.3572 1.8745 4.569s2.8281 1.8925 4.5255 1.8925h35.2c1.6974 0 3.3253-.6807 4.5255-1.8925s1.8745-2.8553 1.8745-4.569v-22.6154c0-1.7137-.6743-3.3572-1.8745-4.569s-2.8281-1.89254-4.5255-1.89254zm-17.6 31.50004c-2.6898 0-5.3192-.8053-7.5558-2.3141-2.2365-1.5087-3.9796-3.6532-5.009-6.1622-1.0293-2.5089-1.2986-5.2697-.7739-7.9332.5248-2.6636 1.8201-5.1101 3.722-7.0304 1.902-1.9203 4.3253-3.228 6.9635-3.7578 2.6381-.5298 5.3726-.2579 7.8577.7813 2.4851 1.0393 4.6091 2.7992 6.1035 5.0572s2.292 4.9127 2.292 7.6284c0 3.6416-1.4329 7.1341-3.9833 9.7091-2.5505 2.575-6.0098 4.0217-9.6167 4.0217z" /></g
                ></svg>
            </div>
          {/if}
          <div>
            <div class="ui-text ui-text-black">{createdUser.name}</div>
            <div class="ui-text">@{createdUser.username}</div>
          </div>
        </div>

        <div class="button-container">
          <Button
            class="created-account-tiktok-event"
            text="Next"
            disabled={!uploadedSrc}
            on:click={() => {
              handleFinished();
            }} />
        </div>
      </div>
    </div>
  {/if}
{/if}
